import React, {useEffect, useState} from 'react';

import {useDispatch, useSelector} from 'react-redux';

import {showModal} from '@/actions/modalActions';
import {updateRefilledBufferVolume} from '@/actions/setupActions';
import {
  cancelRefillBufferVol,
  continueRefillBufferVol,
  stopRefillBufferVol,
} from '@/businesses/refillBufferVolBusiness';
import IntlMessage from '@/components/intlMessage/IntlMessage';
import PopUpButtons from '@/components/popup/PopUpButtons';
import StandalonePopUp from '@/components/popup/StandalonePopUp';
import {REFILL_BUFFER_EXCHANGE_VOL_THRESHOLD} from '@/constants/configurations';
import * as APPDATA from '@/constants/pcConstAppData';
import useIntlFormat from '@/hooks/useIntlFormat';
import {getCurrentAdvSetupParams, getRefillBufferPopup} from '@/selectors/setupSelector';

import './RefillBufferPopup.less';

const DISPLAY_NAME = 'refill-buffer-volume';

/**
 * @enum {string}
 */
const ActionEnum = {
  CONTINUE: `${DISPLAY_NAME}-continue`,
  STOP: `${DISPLAY_NAME}-stop`,
  CANCEL: `${DISPLAY_NAME}-cancel`,
};

function RefillBufferPopup() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntlFormat();
  const {
    transferVolume,
    profileRunningConfig,
    currentBuffer,
    refilledBufferVolume,
    isShowRefillBufferPopUp,
    isPauseHandled,
  } = useSelector(getRefillBufferPopup);

  const {ConfigAdvSetup} = useSelector(getCurrentAdvSetupParams);
  const {GeneralAdvSetup} = ConfigAdvSetup || {};

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    setShowPopup(isShowRefillBufferPopUp);
  }, [isShowRefillBufferPopUp]);

  const popUpButton = [
    {
      id: ActionEnum.CONTINUE,
      label: formatMessage({id: 'modal.refillBufferVol.primaryButton'}),
      isPrimaryButton: true,
      onClick: (e, v) => handleOnClickRefillBuffer(e, v),
      disabled: refilledBufferVolume === APPDATA.EMPTY_FIELD,
      className: '',
    },
    {
      id: ActionEnum.STOP,
      label: formatMessage({id: 'modal.refillBufferVol.secondaryButton'}),
      onClick: (e, v) => handleOnClickRefillBuffer(e, v),
      className: ' redButton ',
    },
    {
      id: ActionEnum.CANCEL,
      label: formatMessage({id: 'modal.refillBufferVol.ternaryButton'}),
      isPrimaryButton: false,
      onClick: (e, v) => handleOnClickRefillBuffer(e, v),
    },
  ];

  // const bufferUsedValue = parseFloat(
  //   Number(
  //     isPauseHandled
  //       ? (profileRunning.Config[APPDATA.KEY.TotalBufferVolumeToUse] ||
  //           profileRunning.Config[APPDATA.KEY.BufferVolumeToUse]) - currentBuffer
  //       : profileRunning.Config[APPDATA.KEY.BufferVolumeToUse] - dialfiltrationStatus.RefilledBufferVolume,
  //   ).toFixed(APPDATA.CONFIGURATION.VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY),
  // );
  // const exchangeVolValue = parseFloat(
  //   Number(profileRunning.Config[APPDATA.KEY.BufferVolumeToUse]).toFixed(
  //     APPDATA.CONFIGURATION.VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY,
  //   ),
  // );
  const bufferNeededValue = parseFloat(
    (
      Number(profileRunningConfig[APPDATA.KEY.BufferVolumeToUse]) -
      Number(transferVolume) +
      REFILL_BUFFER_EXCHANGE_VOL_THRESHOLD
    ).toFixed(APPDATA.CONFIGURATION.VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY),
  );

  const bodyContent = (
    <>
      <IntlMessage
        id={`modal.refillBufferVol.message.${isPauseHandled ? 'pauseHandled' : 'refill'}`}
        value={{
          // bufferUsed: bufferUsedValue,
          // exchangeVol: exchangeVolValue,
          bufferNeeded: bufferNeededValue,
        }}
      />
      <div className="refillBufferInputDiv">
        <div className="middleAlign bufferSensorVolumeDiv">
          <div className="col-md-7">
            <div className="row row-margin">
              <span className="spanStyleInputField">
                <IntlMessage
                  id={
                    !GeneralAdvSetup?.HighVolumeBufferFlag
                      ? 'modal.refillBufferVol.labelInput.measuredVol'
                      : 'modal.refillBufferVol.labelInput.estCurrentBufferVolume'
                  }
                />
              </span>
            </div>
          </div>
          <div className="col-md-4 bufferSensorVolume">
            <span className="spanStyleInputField">{currentBuffer}</span>
          </div>
          <div className="col-md-1">
            <span className="spanStyleInputField unit"> {APPDATA.UNIT.ml} </span>
          </div>
        </div>
        <div className="middleAlign">
          <div className="col-md-7">
            <div className="row row-margin">
              <span
                className="spanStyleInputField"
                disabled={false}
              >
                <IntlMessage id="modal.refillBufferVol.labelInput.refilledBufferVol" />
              </span>
            </div>
          </div>
          <div className="col-md-4">
            <input
              className="inputField"
              id={APPDATA.MESSAGES.BUFFER_EXCHANGE.REFILL.REFILL_BUFFER}
              type="text"
              value={refilledBufferVolume === APPDATA.EMPTY_FIELD ? '' : refilledBufferVolume}
              readOnly
              onClick={(e, v) => onClickRefillBufferVolume(e, v)}
              disabled={false}
            />
          </div>
          <div className="col-md-1">
            <span className="spanStyleInputField unit"> {APPDATA.UNIT.ml} </span>
          </div>
        </div>
      </div>
    </>
  );

  const handleOnClickRefillBuffer = (e) => {
    switch (e.currentTarget.id) {
      case ActionEnum.CONTINUE: {
        dispatch(continueRefillBufferVol(refilledBufferVolume));
        break;
      }
      case ActionEnum.STOP: {
        dispatch(stopRefillBufferVol());
        break;
      }
      case ActionEnum.CANCEL: {
        dispatch(cancelRefillBufferVol());
        break;
      }
      default:
        break;
    }
  };

  const onClickRefillBufferVolume = (e) => {
    dispatch(
      showModal({
        modalHeader: e.currentTarget.id,
        value: refilledBufferVolume === APPDATA.EMPTY_FIELD ? 0 : refilledBufferVolume,
        callback: (e) => {
          dispatch(updateRefilledBufferVolume(e.value));
        },
        maxValue: APPDATA.CONFIGURATION.VOLUME_SENSOR.MAX_VALUE,
        minValue: APPDATA.CONFIGURATION.VOLUME_SENSOR.MIN_VALUE,
        maxDigitAfterDecimal: APPDATA.CONFIGURATION.VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY,
        incrementValue: 0.1,
        unit: APPDATA.UNIT.ml,
      }),
    );
  };

  return (
    <StandalonePopUp
      id={DISPLAY_NAME}
      isPopUpVisible={showPopup}
      contents={{
        popUpTitle: formatMessage({id: 'modal.refillBufferVol.title'}),
        popUpBody: bodyContent,
        popUpFooter: (
          <StandalonePopUp.FooterActions>
            <PopUpButtons data={popUpButton} />
          </StandalonePopUp.FooterActions>
        ),
      }}
      hideCloseButton
    />
  );
}
export default RefillBufferPopup;
